/**
 * Mock home slider and «پیشنهاد / گزارش مشکل» for the dev panel (VITE_USE_MOCKS). Slide images stand
 * in for admin uploads as SVG data URLs (1600×320 wide, 800×400 narrow). Set the `panelContentDemo`
 * flags to see the empty slider or a single slide.
 */
import { FEEDBACK_MAX_CHARS, FEEDBACK_MIN_CHARS, toPersianDigits } from "@mfa-aff/shared";
import type { FeedbackSubmitRequest, PartnerFeedbackView, PartnerSlideView } from "@mfa-aff/shared";
import { ApiError } from "../api/client";

export const panelContentDemo = {
    /** true = no active slide (the home page shows no slider). */
    noSlides: false,
    /** true = only the first slide (no dots or arrows). */
    oneSlide: false,
};

const DAY = 86_400_000;
const ago = (d: number, h = 0) => new Date(Date.now() - d * DAY - h * 3_600_000).toISOString();

/** Deterministic stand-in artwork for an uploaded slide image. */
function slideArt(width: number, height: number, title: string, subtitle: string, colors: [string, string]): string {
    const size = Math.round(height / (width > height * 3 ? 5 : 8));
    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
        `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${colors[0]}"/><stop offset="1" stop-color="${colors[1]}"/></linearGradient></defs>` +
        `<rect width="100%" height="100%" fill="url(#g)"/>` +
        `<circle cx="${width * 0.12}" cy="${height * 0.85}" r="${height * 0.6}" fill="#51B848" opacity="0.22"/>` +
        `<circle cx="${width * 0.95}" cy="${height * 0.05}" r="${height * 0.4}" fill="#FFFFFF" opacity="0.08"/>` +
        `<text x="50%" y="44%" fill="#FFFFFF" font-family="Tahoma, sans-serif" font-size="${size}" font-weight="700" text-anchor="middle" dominant-baseline="middle" direction="rtl">${title}</text>` +
        `<text x="50%" y="66%" fill="#FFFFFF" opacity="0.85" font-family="Tahoma, sans-serif" font-size="${Math.round(size * 0.5)}" text-anchor="middle" dominant-baseline="middle" direction="rtl">${subtitle}</text>` +
        `</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const slide = (id: number, title: string, subtitle: string, colors: [string, string], linkUrl: string | null, withMobile = true): PartnerSlideView => ({
    id,
    title,
    imageUrl: slideArt(1600, 320, title, subtitle, colors),
    mobileImageUrl: withMobile ? slideArt(800, 400, title, subtitle, colors) : null,
    linkUrl,
});

const slides: PartnerSlideView[] = [
    slide(1, "کمپین فروش پاییز", "با هدف فروش، زودتر به سطح بالاتر برسید", ["#2C6FBF", "#003167"], "/special/campaign"),
    slide(2, "هدیهٔ اولین فروش", "اولین فروش این ماه، هدیهٔ نقدی دارد", ["#0B7A4B", "#05402A"], "/levels"),
    slide(3, "آموزش فروش هاست", "راهنمای کوتاه معرفی هاست به دوستانتان", ["#5B3CC4", "#2A1A6B"], "https://mizbanfa.net/blog/", false),
    slide(4, "نرخ‌های تازه از مهر", "بدون لینک: فقط برای اطلاع", ["#B54708", "#7A2E0E"], null),
];

export const slidesMock = (): PartnerSlideView[] => (panelContentDemo.noSlides ? [] : panelContentDemo.oneSlide ? slides.slice(0, 1) : slides);

/** The partner's own messages, newest first. */
const feedback: PartnerFeedbackView[] = [
    {
        id: 12,
        kind: "problem",
        body: "در صفحهٔ کیف پول، بعد از ثبت درخواست برداشت، تا صفحه را دوباره باز نکنم موجودی عوض نمی‌شود.",
        status: "seen",
        reply: null,
        repliedAt: null,
        createdAt: ago(1, 4),
    },
    {
        id: 9,
        kind: "suggestion",
        body: "کاش بنرهای کیت بازاریابی اندازهٔ استوری اینستاگرام هم داشتند.",
        status: "done",
        reply: "ممنون از پیشنهادتان. بنرهای استوری اضافه شد؛ در کیت بازاریابی ببینید.",
        repliedAt: ago(6),
        createdAt: ago(9),
    },
];

let nextId = 13;
let sentThisSession = 0;

export const feedbackMock = (): PartnerFeedbackView[] => feedback.map((f) => ({ ...f }));

export const feedbackSubmitMock = (body: FeedbackSubmitRequest): PartnerFeedbackView => {
    const text = body.body.trim();
    if (text.length < FEEDBACK_MIN_CHARS) throw new ApiError(400, `پیام کوتاه است؛ دست‌کم ${toPersianDigits(FEEDBACK_MIN_CHARS)} کاراکتر بنویسید.`, "body");
    if (text.length > FEEDBACK_MAX_CHARS) throw new ApiError(400, `پیام بلند است؛ حداکثر ${toPersianDigits(FEEDBACK_MAX_CHARS)} کاراکتر.`, "body");
    if (sentThisSession >= 5) throw new ApiError(429, "امروز چند پیام فرستاده‌اید؛ لطفاً کمی بعد دوباره بفرستید.");
    sentThisSession += 1;
    const item: PartnerFeedbackView = {
        id: nextId++,
        kind: body.kind,
        body: text,
        status: "new",
        reply: null,
        repliedAt: null,
        createdAt: new Date().toISOString(),
    };
    feedback.unshift(item);
    return { ...item };
};
